import { Upload, Alert, Button, Spin } from '@/components/ui'
import { InboxOutlined, DeleteOutlined, DownloadOutlined, FileTextOutlined } from '@ant-design/icons'
import type { UploadProps } from 'antd'

interface UploadStepProps {
  eventSlug: string
  file: File | null
  fileName: string
  fileSize: number
  isLoading: boolean
  encodingWarning: boolean
  encoding: string
  onFileSelect: (file: File) => void
  onFileRemove: () => void
}

const { Dragger } = Upload

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export default function UploadStep({
  eventSlug,
  file,
  fileName,
  fileSize,
  isLoading,
  encodingWarning,
  encoding,
  onFileSelect,
  onFileRemove,
}: UploadStepProps) {
  const uploadProps: UploadProps = {
    accept: '.csv',
    maxCount: 1,
    showUploadList: false,
    beforeUpload: (file) => {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        return false
      }
      onFileSelect(file)
      return false // Prevent auto upload
    },
  }

  const handleDownloadTemplate = () => {
    window.location.href = `/events/${eventSlug}/attendees/import/template`
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
      {!file ? (
        <Spin spinning={isLoading}>
          <Dragger {...uploadProps} disabled={isLoading}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ fontSize: 48, color: '#132B51' }} />
            </p>
            <p className="ant-upload-text" style={{ fontSize: '16px', fontWeight: 500 }}>
              Click or drag CSV file to upload
            </p>
            <p className="ant-upload-hint" style={{ fontSize: '14px', color: '#6b7280' }}>
              Maximum file size: 10MB. Only CSV files are supported.
            </p>
          </Dragger>
        </Spin>
      ) : (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '40px', height: '40px', backgroundColor: '#dcfce7', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileTextOutlined style={{ color: '#16a34a', fontSize: '18px' }} />
              </div>
              <div>
                <p style={{ fontWeight: 500, color: '#111827' }}>{fileName}</p>
                <p style={{ fontSize: '14px', color: '#6b7280' }}>{formatBytes(fileSize)}</p>
              </div>
            </div>
            <Button
              variant="tertiary"
              icon={<DeleteOutlined />}
              onClick={onFileRemove}
              disabled={isLoading}
            >
              Remove
            </Button>
          </div>
        </div>
      )}

      {encodingWarning && (
        <Alert
          type="warning"
          message="Encoding Warning"
          description={`The file appears to be encoded in ${encoding}. It will be converted to UTF-8. Some special characters may not display correctly.`}
          showIcon
        />
      )}

      <div style={{ paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
        <Button
          variant="tertiary"
          icon={<DownloadOutlined />}
          onClick={handleDownloadTemplate}
        >
          Download CSV Template
        </Button>
        <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px' }}>
          Download a template with the correct column headers for your event's registration fields.
        </p>
      </div>
    </div>
  )
}
